import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCartStore } from "../store/cartStore";
import axios from "../utils/axios";
import toast from "react-hot-toast";

// Auth context and utilities
import { useAuth } from "../contexts/AuthContext";
import {
  canUserPurchase,
  createLoginRedirectUrl,
  getRoleName,
} from "../utils/authUtils";

// Components
import PurchaseProtection from "../components/PurchaseProtection";

// Demo Requirement #6: Cart functionality
const CartPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [shippingAddress, setShippingAddress] = useState("");
  const { isAuthenticated, user } = useAuth();

  // Default placeholder image as data URL to avoid external network calls
  const defaultProductImage =
    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0zMiAyOEgxNlYyNEgyNFYxNkgzMlYyOFpNNDggMjhIMzJWMjRINDBWMTZINDhWMjhaIiBmaWxsPSIjOUI5QkE4Ii8+CjxwYXRoIGQ9Ik0zMiA0NEgxNlY0MEgyNFYzMkgzMlY0NFpNNDggNDRIMzJWNDBINDBWMzJINDhWNDRaIiBmaWxsPSIjOUI5QkE4Ii8+CjxwYXRoIGQ9Ik0zMiA2MEgxNlY1NkgyNFY0OEgzMlY2MFpNNDggNjBIMzJWNTZINDBWNDhINDhWNjBaIiBmaWxsPSIjOUI5QkE4Ii8+Cjx0ZXh0IHg9IjQwIiB5PSI0NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSIjNkI3Mjg0IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5Qcm9kdWN0PC90ZXh0Pgo8L3N2Zz4K";

  const {
    cart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemCount,
    getCartItems,
  } = useCartStore();

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      toast.success("Item removed from cart");
    } else {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleRemoveItem = (productId, productName) => {
    removeFromCart(productId);
    toast.success(`${productName} removed from cart`);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    // Check authentication and authorization
    if (!isAuthenticated) {
      toast.error("Please login to proceed with checkout");
      const loginUrl = createLoginRedirectUrl(
        location.pathname + location.search
      );
      navigate(loginUrl);
      return;
    }

    if (!canUserPurchase(user)) {
      const userRole = getRoleName(user.role);
      toast.error(
        `${userRole} accounts cannot make purchases. Only customer accounts can checkout.`
      );
      return;
    }

    setIsProcessing(true);

    try {
      // Check if all items have payment links
      const allItemsHavePaymentLinks = cart.every((item) => item.payment_link);
      const someItemsHavePaymentLinks = cart.some((item) => item.payment_link);

      // If all items have payment links, redirect to external payment
      if (allItemsHavePaymentLinks) {
        // If single item, redirect directly
        if (cart.length === 1) {
          window.open(cart[0].payment_link, "_blank");
          toast.success("Redirecting to payment gateway...");
          setIsProcessing(false);
          return;
        } else {
          // Multiple items with payment links - show selection
          toast.error(
            "Please purchase items with external payment links separately."
          );
          setIsProcessing(false);
          return;
        }
      }

      // If some items have payment links, show error
      if (someItemsHavePaymentLinks) {
        toast.error(
          "Cannot mix items with external payment links. Please checkout separately."
        );
        setIsProcessing(false);
        return;
      }

      // Standard internal checkout for items without payment links
      const totalAmount = getCartTotal();
      const items = getCartItems();

      // Create order
      const response = await axios.post("/api/orders", {
        items,
        totalAmount,
        shippingAddress: shippingAddress || "Not provided",
      });

      toast.success("Order placed successfully!");
      clearCart();

      // Redirect to thank you page
      navigate("/thank-you", {
        state: {
          message: "Order placed successfully!",
          orderId: response.data.order.id,
        },
      });
    } catch (error) {
      console.error("Checkout error:", error);
      const errorMessage =
        error.response?.data?.message || "Order failed. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
            <h2 className="mt-2 text-xl font-medium text-gray-900">
              Your cart is empty
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Start shopping to add items to your cart.
            </p>
            <div className="mt-6 space-y-4">
              <button
                onClick={() => navigate("/products")}
                className="btn-primary btn-lg"
              >
                Browse Products
              </button>
              <div>
                <button
                  onClick={() => navigate("/register")}
                  className="btn-secondary btn-md mr-4"
                >
                  Join Sales Call
                </button>
                <button
                  onClick={() => navigate("/appointments")}
                  className="btn-secondary btn-md"
                >
                  Book Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Login Required Notice for Non-Authenticated Users */}
        {!isAuthenticated && cart.length > 0 && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-blue-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Login Required for Checkout
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Please login with a customer account to proceed with
                    checkout. Your cart items are saved and will be preserved
                    after login.
                  </p>
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => {
                      const loginUrl = createLoginRedirectUrl(
                        location.pathname + location.search
                      );
                      navigate(loginUrl);
                    }}
                    className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    Login Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Purchase Restriction Notice for Non-Customer Users */}
        {isAuthenticated && !canUserPurchase(user) && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Purchase Restricted
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    You are logged in as{" "}
                    <strong>{getRoleName(user.role)}</strong>. Only customer
                    accounts can make purchases. If you need to buy products,
                    please create a customer account or contact support.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Shopping Cart</h1>
          <p className="text-gray-600 mt-2">
            {getCartItemCount()} {getCartItemCount() === 1 ? "item" : "items"}{" "}
            in your cart
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  Cart Items
                </h2>
              </div>

              <div className="divide-y divide-gray-200">
                {cart.map((item) => (
                  <div key={item.id} className="p-6">
                    <div className="flex items-center">
                      {/* Product Image */}
                      <div className="w-20 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={item.image_url || defaultProductImage}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = defaultProductImage;
                          }}
                        />
                      </div>

                      {/* Product Details */}
                      <div className="ml-4 flex-1">
                        <h3 className="text-lg font-medium text-gray-900">
                          {item.name}
                        </h3>
                        {item.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {item.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          {item.category && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                              {item.category}
                            </span>
                          )}
                          {item.payment_link && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              💳 External Payment
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Price and Quantity */}
                      <div className="ml-4 flex items-center space-x-4">
                        {/* Quantity Controls */}
                        <div className="flex items-center border border-gray-300 rounded-md">
                          <button
                            onClick={() =>
                              handleQuantityChange(item.id, item.quantity - 1)
                            }
                            className="p-1 hover:bg-gray-100 text-gray-600"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20 12H4"
                              />
                            </svg>
                          </button>
                          <span className="px-3 py-1 text-sm font-medium text-gray-900 min-w-[2rem] text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              handleQuantityChange(item.id, item.quantity + 1)
                            }
                            className="p-1 hover:bg-gray-100 text-gray-600"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                              />
                            </svg>
                          </button>
                        </div>

                        {/* Price */}
                        <div className="text-right">
                          <div className="text-lg font-semibold text-gray-900">
                            ₹
                            {(Number(item.price || 0) * item.quantity).toFixed(
                              2
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            ₹{Number(item.price || 0).toFixed(2)} each
                          </div>
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => handleRemoveItem(item.id, item.name)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Remove item"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm sticky top-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  Order Summary
                </h2>
              </div>

              <div className="p-6 space-y-4">
                {/* Cart Summary */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Subtotal ({getCartItemCount()} items)
                    </span>
                    <span className="text-gray-900">
                      ₹{getCartTotal().toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Shipping</span>
                    <span className="text-gray-900">Free</span>
                  </div>
                  <div className="border-t border-gray-200 pt-2">
                    <div className="flex justify-between">
                      <span className="text-lg font-medium text-gray-900">
                        Total
                      </span>
                      <span className="text-lg font-medium text-gray-900">
                        ₹{getCartTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div>
                  <label
                    htmlFor="shipping"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Shipping Address (Optional)
                  </label>
                  <textarea
                    id="shipping"
                    value={shippingAddress}
                    onChange={(e) => setShippingAddress(e.target.value)}
                    rows={3}
                    className="input"
                    placeholder="Enter your shipping address..."
                  />
                </div>

                {/* Checkout Button */}
                <PurchaseProtection
                  onPurchaseAction={handleCheckout}
                  fallbackText="Login to Proceed to Checkout"
                  productName="items in cart"
                >
                  <button
                    onClick={handleCheckout}
                    disabled={isProcessing}
                    className="w-full btn-primary btn-lg"
                  >
                    {isProcessing ? (
                      <div className="flex items-center justify-center">
                        <div className="loading-dots mr-2">
                          <div></div>
                          <div></div>
                          <div></div>
                          <div></div>
                        </div>
                        Processing...
                      </div>
                    ) : cart.some((item) => item.payment_link) ? (
                      "💳 Proceed to External Payment"
                    ) : (
                      "Proceed to Checkout"
                    )}
                  </button>
                </PurchaseProtection>

                {/* Additional Options */}
                <div className="space-y-2">
                  <button
                    onClick={() => navigate("/appointments")}
                    className="w-full btn-secondary btn-md text-sm"
                  >
                    📅 Need help? Book consultation
                  </button>

                  <button
                    onClick={() => navigate("/register")}
                    className="w-full btn-secondary btn-md text-sm"
                  >
                    🎥 Join quick sales call
                  </button>
                </div>

                {/* Trust Indicators */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="text-center space-y-2">
                    <div className="flex items-center justify-center text-sm text-gray-600">
                      <svg
                        className="w-4 h-4 mr-1 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Secure checkout
                    </div>
                    <div className="flex items-center justify-center text-sm text-gray-600">
                      <svg
                        className="w-4 h-4 mr-1 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Money-back guarantee
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Continue Shopping */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate("/products")}
            className="btn-secondary btn-md"
          >
            ← Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
